/**
 * Serwis API dla modułu Magazyny
 * Zarządzanie magazynami (CRUD), przypisaniami pracownikow,
 * stanami magazynowymi per-magazyn oraz przesunięciami miedzymagazynowymi.
 *
 * UWAGA: Ten plik zastepuje rowniez dawny multiWarehouseService.js
 * Funkcje z multiWarehouseService zostaly przeniesione tutaj.
 */

import api from './api';

export const warehousesService = {
  // =====================================================================
  // CRUD magazynow
  // =====================================================================

  // Pobierz wszystkie magazyny (zwraca tablice bezposrednio - uzywane przez KolektorPage, ProductsPage, LocationPricingPage)
  getWarehouses: async () => {
    try {
      const response = await api.get('/warehouses');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd pobierania magazynów');
    }
  },

  // Pobierz wszystkie magazyny (zwraca { success, data } - uzywane przez WarehouseManagement, InventoryTable, InterWarehouseTransfer)
  /**
   * Magazyny mogace byc CELEM przesuniecia - cala siec, bez filtra lokalizacji.
   * GET /warehouses zwraca tylko magazyny lokalizacji uzytkownika, wiec do
   * wyboru celu transferu potrzebny jest osobny endpoint (chroniony
   * uprawnieniem magazyn/przesuniecia).
   */
  getTransferTargets: async () => {
    const response = await api.get('/warehouses/cele-transferu');
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    };
  },

  getWarehousesWithStatus: async () => {
    try {
      const response = await api.get('/warehouses');
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Pobierz magazyny dla lokalizacji
  getWarehousesByLocation: async (locationId) => {
    try {
      const response = await api.get(`/warehouses/location/${locationId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd pobierania magazynów dla lokalizacji');
    }
  },

  // Pobierz szczegóły magazynu
  getWarehouseDetails: async (warehouseId) => {
    try {
      const response = await api.get(`/warehouses/${warehouseId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd pobierania szczegółów magazynu');
    }
  },

  // Dodaj nowy magazyn (throw pattern)
  addWarehouse: async (warehouseData) => {
    try {
      const response = await api.post('/warehouses', warehouseData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd dodawania magazynu');
    }
  },

  // Tworzy nowy magazyn (zwraca { success, data })
  createWarehouse: async (warehouseData) => {
    try {
      const response = await api.post('/warehouses', warehouseData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Zaktualizuj magazyn (throw pattern)
  updateWarehouse: async (warehouseId, warehouseData) => {
    try {
      const response = await api.put(`/warehouses/${warehouseId}`, warehouseData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd aktualizacji magazynu');
    }
  },

  // Aktualizuje magazyn (zwraca { success, data })
  updateWarehouseWithStatus: async (warehouseId, warehouseData) => {
    try {
      const response = await api.put(`/warehouses/${warehouseId}`, warehouseData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Usuń magazyn (throw pattern)
  deleteWarehouse: async (warehouseId) => {
    try {
      const response = await api.delete(`/warehouses/${warehouseId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Błąd usuwania magazynu');
    }
  },

  // Usun magazyn - soft delete (zwraca { success, message })
  deleteWarehouseWithStatus: async (warehouseId) => {
    try {
      const response = await api.delete(`/warehouses/${warehouseId}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // =====================================================================
  // Przypisania pracownikow do magazynow
  // =====================================================================

  // Pobiera przypisania pracownikow do magazynow
  getEmployeeAssignments: async (warehouseId = null) => {
    try {
      const url = warehouseId
        ? `/warehouses/${warehouseId}/employees`
        : '/warehouses/employees';

      const response = await api.get(url);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Przypisuje pracownika do magazynu
  assignEmployee: async (warehouseId, userLogin, role = 'pracownik', permissions = '') => {
    try {
      const response = await api.post(`/warehouses/${warehouseId}/employees`, {
        user_login: userLogin,
        rola: role,
        uprawnienia: permissions
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Usuwa przypisanie pracownika do magazynu
  removeEmployeeAssignment: async (warehouseId, userLogin) => {
    try {
      const response = await api.delete(`/warehouses/${warehouseId}/employees/${userLogin}`);
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // =====================================================================
  // Stany magazynowe per magazyn
  // =====================================================================

  // Pobiera stany magazynowe dla konkretnego magazynu
  getWarehouseInventory: async (warehouseId, params = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        search = ''
      } = params;

      const response = await api.get(`/warehouses/${warehouseId}/inventory`, {
        params: { page, limit, search }
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Aktualizuje stan magazynowy dla konkretnego magazynu
  updateWarehouseStock: async (warehouseId, productId, stockData) => {
    try {
      const response = await api.put(`/warehouses/${warehouseId}/inventory/${productId}`, stockData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // =====================================================================
  // Przesunięcia miedzymagazynowe (transfery)
  // =====================================================================

  // Pobiera przesunięcia miedzymagazynowe
  getTransfers: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 20,
        status = '',
        warehouse_id = ''
      } = params;

      const response = await api.get('/warehouses/transfers', {
        params: { page, limit, status, warehouse_id }
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Tworzy nowe przesunięcie miedzymagazynowe
  createTransfer: async (transferData) => {
    try {
      const response = await api.post('/warehouses/transfers', transferData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Aktualizuje status przesunięcia
  updateTransferStatus: async (transferId, action, items = []) => {
    try {
      const response = await api.put(`/warehouses/transfers/${transferId}/${action}`, {
        items
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Pobiera szczegoly przesunięcia
  getTransferDetails: async (transferId) => {
    try {
      const response = await api.get(`/warehouses/transfers/${transferId}`);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  },

  // =====================================================================
  // Uzytkownicy
  // =====================================================================

  // Pobiera uzytkownikow dostepnych do przypisania
  getAvailableUsers: async () => {
    try {
      const response = await api.get('admin/users');
      return {
        success: true,
        data: response.data.data?.users || response.data.data || response.data,
        message: response.data.message || 'Pobrano użytkowników'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }
};

export default warehousesService;
